/**
 * Offline data loader for Capacitor builds.
 *
 * Capacitor: fetches .dat (XOR+gzip encoded), decodes in-memory, returns JSON.
 * Web: fetches .json with version-based cache busting. Static data is trusted
 *      forever — cache is busted only on deploy via ?v= param.
 */
import { gunzipSync } from 'fflate';
import i18n from '@/i18n';

const APP_ID = 'com.americanhealthprices.app';
const SALT = 'ahp-offline-2026';
const KEY_LEN = 256;

/** Build-time version stamp — changes on each deploy to bust browser cache */
const DATA_VERSION = import.meta.env.VITE_DATA_VERSION || '1';
const CDN_BASE = 'https://static.americanhealthprices.com/data';

/**
 * Remote data-version manifest — lets a pipeline R2 refresh reach users
 * WITHOUT an app redeploy. The build-time DATA_VERSION only changes when a
 * new bundle ships (and the service worker actually updates); before this,
 * three stacked caches (SW static-data 1yr → HTTP immutable → old ?v=) pinned
 * returning users to pre-refresh data indefinitely.
 *
 * The pipeline's r2 step uploads config/data-version.json ({"v":"<epoch>"}).
 * We fetch it no-cache at first use; until it resolves, the build stamp is
 * used (correct for first-time visitors, upgraded mid-session for returners).
 */
const DATA_VERSION_URL = 'https://static.americanhealthprices.com/config/data-version.json';
let _remoteDataVersion: string | null = null;
let _dvFetchStarted = false;

function fetchRemoteDataVersion() {
  if (_dvFetchStarted) return;
  _dvFetchStarted = true;
  fetch(DATA_VERSION_URL, { cache: 'no-cache' })
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d?.v) _remoteDataVersion = String(d.v); })
    .catch(() => {});
}

function currentDataVersion(): string {
  fetchRemoteDataVersion();
  // Prefer whichever is NEWER (both are epoch-ms stamps): a fresh deploy can
  // postdate the last pipeline run and vice versa.
  if (_remoteDataVersion && /^\d+$/.test(_remoteDataVersion) && /^\d+$/.test(DATA_VERSION)) {
    return Number(_remoteDataVersion) > Number(DATA_VERSION) ? _remoteDataVersion : DATA_VERSION;
  }
  return _remoteDataVersion || DATA_VERSION;
}

/**
 * fetch() with a hard timeout. A stalled connection (common on mobile/cellular)
 * otherwise leaves promises pending forever, which surfaces as an infinite
 * spinner. AbortController guarantees the await always settles.
 */
async function fetchWithTimeout(url: string, ms = 20000, init?: RequestInit): Promise<Response> {
  if (typeof AbortController === 'undefined') return fetch(url, init);
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

function isCapacitor(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.protocol;
  return p === 'capacitor:' || p === 'ionic:';
}

/**
 * Derive a 256-byte XOR key from filename (must match encode-offline-data.mjs).
 * Uses a simple iterative hash since SubtleCrypto is async and we want sync.
 */
async function deriveKey(filename: string): Promise<Uint8Array> {
  const seed = `${APP_ID}:${SALT}:${filename}`;
  const parts: Uint8Array[] = [];
  for (let i = 0; i < 4; i++) {
    const data = new TextEncoder().encode(`${seed}:${i}`);
    const hash = await crypto.subtle.digest('SHA-512', data);
    parts.push(new Uint8Array(hash));
  }
  const key = new Uint8Array(KEY_LEN);
  let offset = 0;
  for (const p of parts) {
    key.set(p.subarray(0, Math.min(p.length, KEY_LEN - offset)), offset);
    offset += p.length;
    if (offset >= KEY_LEN) break;
  }
  return key;
}

function xorDecode(data: Uint8Array, key: Uint8Array): Uint8Array {
  const out = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) {
    out[i] = data[i] ^ key[i % key.length];
  }
  return out;
}

/**
 * Check if the app should use live DB instead of static JSON.
 * Priority: URL param (?db=live or ?live) > remote config > default (static)
 *
 * Remote config: static.americanhealthprices.com/config/data-mode.json
 * Format: { "mode": "db" | "static" }
 * Update that file to toggle all users without redeploying.
 */
let _remoteMode: 'db' | 'static' | null = null;
let _remoteFetchedAt = 0;
const REMOTE_CONFIG_URL = 'https://static.americanhealthprices.com/config/data-mode.json';

// Background fetch — non-blocking, cached for 2 minutes
function fetchRemoteMode() {
  if (Date.now() - _remoteFetchedAt < 120_000) return; // cached
  _remoteFetchedAt = Date.now();
  fetch(REMOTE_CONFIG_URL, { cache: 'no-cache' })
    .then(r => r.ok ? r.json() : null)
    .then(d => { if (d?.mode) _remoteMode = d.mode; })
    .catch(() => {});
}

export function useLiveDb(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  // URL overrides
  if (params.get('db') === 'live' || params.has('live')) return true;
  if (params.has('static')) return false;
  // Kick off remote config fetch (non-blocking)
  fetchRemoteMode();
  // Use remote config if available
  if (_remoteMode === 'db') return true;
  return false;
}

export interface LoadStaticDataOptions {
  /** Skip the bundled/origin copy and go straight to the CDN. For files the
   *  pipeline refreshes on R2 between deploys and that are deliberately kept
   *  OUT of the Pages bundle (codes_stats_*) — the origin probe would only
   *  ever return the SPA's index.html, a wasted round-trip per open. */
  cdnOnly?: boolean;
}

/**
 * Load a static data file.
 *
 * @returns parsed JSON of type T, or null if the web data is stale / missing
 *          (caller should fall through to Supabase).
 */
export async function loadStaticData<T>(fileName: string, opts?: LoadStaticDataOptions): Promise<T | null> {
  // ?db=live bypasses all static data
  if (useLiveDb()) return null;
  const cdnOnly = !!opts?.cdnOnly;
  if (isCapacitor()) {
    // Native data model: the most-important data is baked into the build as
    // encoded .dat (instant, works offline); everything else (per-scenario /
    // per-hospital detail, coverage, code pages) is fetched as external JSON
    // from the CDN on load. So: try the bundled .dat first, then fall back to
    // the CDN for non-bundled files.
    const datName = fileName.replace(/\.json$/, '.dat');
    if (!cdnOnly) try {
      const resp = await fetchWithTimeout(`/data/${datName}`);
      if (resp.ok) {
        const buf = new Uint8Array(await resp.arrayBuffer());
        const key = await deriveKey(fileName);
        const decompressed = gunzipSync(xorDecode(buf, key));
        return JSON.parse(new TextDecoder().decode(decompressed)) as T;
      }
    } catch (e) {
      console.warn(`[offlineData] bundled .dat read failed for ${datName}, trying CDN:`, e);
    }
    // Not bundled (or decode failed) → external JSON from the CDN. Requires a
    // connection; offline + not-bundled returns null and the screen degrades
    // gracefully ("connect to see hospital prices").
    try {
      const cdn = await fetchWithTimeout(`${CDN_BASE}/${fileName}?v=${currentDataVersion()}`);
      if (cdn.ok) return await cdn.json() as T;
      console.warn(`[offlineData] CDN fetch failed for ${fileName}:`, cdn.status);
    } catch (e) {
      console.warn(`[offlineData] CDN unreachable for ${fileName} (offline?):`, e);
    }
    return null;
  }

  // Web: fetch .json with version-based cache busting — try local first, then CDN
  const vParam = `?v=${currentDataVersion()}`;
  let resp: Response | null = null;
  let isJson = false;
  if (!cdnOnly) {
    resp = await fetchWithTimeout(`/data/${fileName}${vParam}`);
    // Check content-type to avoid SPA fallback returning HTML for missing files
    const ct = resp.headers.get('content-type') || '';
    isJson = ct.includes('json') || ct.includes('octet');
  }

  if (!resp || !resp.ok || !isJson) {
    // Local file missing (e.g. per-hospital data not in dist build) — try CDN
    try {
      resp = await fetchWithTimeout(`${CDN_BASE}/${fileName}${vParam}`);
    } catch {
      return null;
    }
    if (!resp.ok) return null;
  }

  let json: any;
  try {
    json = await resp.json();
  } catch {
    return null; // Not valid JSON
  }

  // No staleness check — static data is trusted until next deploy changes DATA_VERSION
  return json as T;
}

/**
 * Load a static JSON file with language awareness.
 * Tries the translated version first (e.g., scenarios_index_es.json),
 * falls back to the English base file if not available.
 *
 * For per-scenario detail files, use the {uuid}_{lang}.json pattern.
 */
export async function loadTranslatedStaticData<T>(fileName: string): Promise<T | null> {
  const lang = (i18n.language || 'en').substring(0, 2);

  if (lang !== 'en') {
    // Try translated file first: scenarios_index.json → scenarios_index_es.json
    const translatedName = fileName.replace(/\.json$/, `_${lang}.json`);
    const result = await loadStaticData<T>(translatedName);
    if (result) return result;
  }

  // Fallback to English
  return loadStaticData<T>(fileName);
}
